import { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft } from "@untitledui/icons";
import { documentService } from "@/data/mock-documents";
import { useDocumentPolygons } from "@/hooks/use-document-polygons";
import { PdfViewer } from "@/components/application/pdf-viewer/pdf-viewer";
import { PolygonManager } from "@/components/application/pdf-viewer/polygon-manager";
import { AnnotationDrawer } from "@/components/application/pdf-viewer/annotation-drawer";
import type { ReviewPolygon } from "@/types/document-review";
// PDF di test ora servito dalla cartella public
const testPdfUrl = "/document-review-test.pdf";

export default function ReceiverDocumentReviewPage() {
    const { containerId, documentId } = useParams<{ containerId: string; documentId: string }>();
    const { polygons, selectedPolygon, createPolygon, updatePolygon, deletePolygon, selectPolygon, setSelectedPolygon } = useDocumentPolygons(documentId);
    const [currentPage, setCurrentPage] = useState(1);

    // Wrapper per selezionare poligono e cambiare pagina
    const handleSelectPolygon = useCallback((polygonId: string) => {
        const polygon = polygons.find(p => p.id === polygonId);
        if (polygon) {
            // Prima seleziona il poligono (per assicurarsi che sia nello stato)
            selectPolygon(polygonId);
            // Poi cambia pagina alla pagina del poligono
            // Usa requestAnimationFrame per assicurarsi che la selezione avvenga prima del cambio pagina
            requestAnimationFrame(() => {
                setCurrentPage(polygon.pageNumber);
                // Seleziona nuovamente dopo il cambio pagina per assicurarsi che il poligono sia selezionato sulla nuova pagina
                setTimeout(() => {
                    selectPolygon(polygonId);
                }, 100);
            });
        }
    }, [polygons, selectPolygon]);

    const doc = useMemo(() => (documentId ? documentService.getById(documentId) : undefined), [documentId]);

    // Handle creating a new polygon
    const handleCreatePolygon = useCallback((options?: { pageNumber?: number; x?: number; y?: number }) => {
        if (!documentId) return;

        const pageNumber = options?.pageNumber ?? currentPage;
        const x = options?.x ?? 0.416;
        const y = options?.y ?? 0.464;

        createPolygon({
            pageNumber,
            x,
            y,
            width: 0.168,
            height: 0.071,
            notes: "",
            attributes: {},
            comments: [],
        });
    }, [documentId, currentPage, createPolygon]);

    // Handle updating a polygon
    const handleUpdatePolygon = useCallback((updates: Partial<ReviewPolygon>) => {
        if (!selectedPolygon) return;
        updatePolygon(selectedPolygon.id, updates);
    }, [selectedPolygon, updatePolygon]);

    const fileUrl = testPdfUrl as string;

    return (
        <div className="h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <Link to={`/receiver/containers/${containerId}`} className="text-tertiary hover:text-primary">
                        <ArrowLeft className="size-5" />
                    </Link>
                    <div>
                        <h1 className="text-xl font-semibold text-primary">Revisione Documento</h1>
                        <div className="text-sm text-tertiary">{doc?.name || "Documento"}</div>
                    </div>
                </div>
            </div>
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-0">
                <div className="relative">
                    <PdfViewer
                        fileUrl={fileUrl}
                        polygons={polygons}
                        onCreatePolygon={handleCreatePolygon}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                        renderOverlay={({ pageNumber, pageWidth, pageHeight }) => (
                            <PolygonManager
                                pageNumber={pageNumber}
                                pageWidth={pageWidth}
                                pageHeight={pageHeight}
                                polygons={polygons}
                                selectedPolygonId={selectedPolygon?.id || null}
                                onChange={updatePolygon}
                                onSelect={selectPolygon}
                                onDelete={deletePolygon}
                            />
                        )}
                    />
                </div>
                <AnnotationDrawer
                    isOpen={true}
                    polygon={selectedPolygon}
                    allPolygons={polygons}
                    documentId={documentId || "1"}
                    onClose={() => setSelectedPolygon(null)}
                    onUpdate={handleUpdatePolygon}
                    onSelectPolygon={handleSelectPolygon}
                    onDelete={deletePolygon}
                    onMarkResolved={(id, resolved) => {
                        const polygon = polygons.find(p => p.id === id);
                        if (!polygon) return;
                        updatePolygon(id, {
                            attributes: { ...polygon.attributes, resolved: String(resolved) }
                        });
                    }}
                />
            </div>
        </div>
    );
}


